import * as React from 'react'
import { View, Text, ScrollView, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Tag from '@components/tag/Tag'
import ModalHeader from '@components/filter/ModalHeader'
import Drawer from '@components/drawer/Drawer'
import Button from '@components/button/Button'
import TagSection from '@components/filter/TagSection'
import { CalendarDatePickerComponent as Calendar } from '@components/picker/CalendarDatePicker'

import ManifestationFilter from './ControleManifestationFilter'
import MetierFilter from '@components/filter/MetierFilter'
import SocieteFilter from '@components/filter/SocieteFilter'

import useReducer from '@store/useReducer'
import * as FiltreStore from '@store/filtre/filtre'

import useI18n from '@i18n/useI18n'

import { ControleQualiteTypeControle } from '@api/enum'

interface Props {
  filtres: ControleFilters
  onFiltre: (part: Partial<ControleFilters>) => void
  onReset: () => void
  display: (keyof ControleFilters)[]
}

const PERIODS = ['all', 'year', 'halfYear', 'month'] as const
const TYPES = [ControleQualiteTypeControle.VIPARIS, ControleQualiteTypeControle.AUTO] as const

const GroupedFilterLayout = ({ filtres, onFiltre, onReset, display }: Props) => {
  const i18n = useI18n()

  const metiers = useReducer(FiltreStore.store, (s) => s.metiers)
  const manifestations = useReducer(FiltreStore.store, (s) => s.manifestations)
  const societes = useReducer(FiltreStore.store, (s) => s.societes)

  const [sub, setSub] = React.useState<'manifestation' | 'metier' | 'societe' | 'date'>()
  const [local, setLocal] = React.useState(filtres)

  const localMetiers = React.useMemo(() => metiers.filter((a) => local.metier.includes(a.id)), [metiers, local.metier])

  const localSocietes = React.useMemo(
    () => societes.filter((a) => local.societe.includes(a.id)),
    [societes, local.societe]
  )

  const localManifestations = React.useMemo(
    () => manifestations.filter((a) => local.manifestation.includes(a.id)),
    [manifestations, local.manifestation]
  )

  const renderSub = () => {
    if (sub === 'metier') {
      return (
        <MetierFilter
          metiers={metiers}
          onMetiersSelected={(values) => setLocal({ ...local, metier: values })}
          selected={local.metier}
          style="list"
        />
      )
    }

    if (sub === 'manifestation') {
      return (
        <ManifestationFilter
          manifestations={manifestations}
          onManifestationsSelected={(values) => setLocal({ ...local, manifestation: values })}
          selected={local.manifestation}
          style="list"
        />
      )
    }

    if (sub === 'societe') {
      return (
        <SocieteFilter
          societes={societes}
          onSocietesSelected={(values) => setLocal({ ...local, societe: values })}
          selected={local.societe}
          style="list"
        />
      )
    }

    return (
      <Calendar
        start={local.from ? new Date(local.from) : undefined}
        end={local.to ? new Date(local.to) : undefined}
        onDates={(start, end) =>
          setLocal({
            ...local,
            period: !start && !end ? 'all' : undefined,
            from: start?.toISOString(),
            to: end?.toISOString(),
          })
        }
        onClose={() => setSub(undefined)}
        max={new Date()}
        required={false}
      />
    )
  }

  const renderList = () => {
    return (
      <ScrollView contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 16 }}>
        {/* MANIFESTATION */}
        {display.includes('manifestation') && (
          <TagSection
            title={i18n.t('filters.tags.manifestation')}
            link={{
              label: i18n.t('filters.link.manifestation'),
              onClick: () => setSub('manifestation'),
            }}
            items={localManifestations.map((m) => m.libelle)}
            onDelete={(index) =>
              setLocal({
                ...local,
                manifestation: localManifestations.filter((id, idx) => idx !== index).map((m) => m.id),
              })
            }
          />
        )}

        {/* METIER */}
        {display.includes('metier') && (
          <TagSection
            title={i18n.t('filters.tags.metier')}
            link={{
              label: i18n.t('filters.link.metier'),
              onClick: () => setSub('metier'),
            }}
            items={localMetiers.map((m) => m.libelle)}
            onDelete={(index) =>
              setLocal({
                ...local,
                metier: localMetiers.filter((id, idx) => idx !== index).map((m) => m.id),
              })
            }
          />
        )}

        {/* TYPE */}
        {display.includes('type') && (
          <>
            <Title>{i18n.t('filters.tags.type')}</Title>
            <LineSection>
              {TYPES.map((item) => (
                <FilterContainer key={item}>
                  <Tag
                    item={{ label: i18n.t(`filters.controle.type.${item}`), value: item }}
                    onItemSelected={() =>
                      setLocal({
                        ...local,
                        type: local.type === item ? undefined : item,
                      })
                    }
                    selected={local.type === item}
                    inactiveColor
                  />
                </FilterContainer>
              ))}
            </LineSection>
          </>
        )}

        {/* SOCIETE */}
        {display.includes('societe') && (
          <TagSection
            title={i18n.t('filters.tags.societe')}
            link={{
              label: i18n.t('filters.link.societe'),
              onClick: () => setSub('societe'),
            }}
            items={localSocietes.map((m) => m.libelle)}
            onDelete={(index) =>
              setLocal({
                ...local,
                societe: localSocietes.filter((id, idx) => idx !== index).map((m) => m.id),
              })
            }
          />
        )}

        {/* PERIOD */}
        {display.includes('period') && (
          <>
            <Title>{i18n.t('filters.tags.date')}</Title>
            <LineSection>
              {PERIODS.map((item) => (
                <FilterContainer key={item}>
                  <Tag
                    item={{ label: i18n.t(`screens.controleDetail.sort.${item}`), value: item }}
                    onItemSelected={() =>
                      setLocal({
                        ...local,
                        from: undefined,
                        to: undefined,
                        period: item,
                      })
                    }
                    selected={local.period === item}
                    inactiveColor
                  />
                </FilterContainer>
              ))}
            </LineSection>
          </>
        )}

        {/* DATES */}
        {display.includes('from') && display.includes('to') && (
          <Line activeOpacity={0.8} onPress={() => setSub('date')}>
            <DateInfo>{i18n.t('components.datePicker.from')}</DateInfo>
            <DateContainer>
              <DateInfo>
                {local.from
                  ? i18n.t('components.datePicker.date', { date: new Date(local.from) })
                  : i18n.t('components.datePicker.empty')}
              </DateInfo>
            </DateContainer>
            <DateInfo>{i18n.t('components.datePicker.to')}</DateInfo>
            <DateContainer>
              <DateInfo>
                {local.to
                  ? i18n.t('components.datePicker.date', { date: new Date(local.to) })
                  : i18n.t('components.datePicker.empty')}
              </DateInfo>
            </DateContainer>
          </Line>
        )}
      </ScrollView>
    )
  }

  if (sub === 'date') {
    return renderSub()
  }

  if (!!sub) {
    return (
      <>
        <DrawerHeader>
          <ModalHeader
            left={{ name: 'arrow-left', onClick: () => setSub(undefined) }}
            title={i18n.t(`filters.tags.${sub}`)}
            right={{
              label: i18n.t('common.erase'),
              onClick: () => setLocal({ ...local, [sub]: [] }),
            }}
          />
        </DrawerHeader>

        <Full>{renderSub()}</Full>

        <ButtonContainer>
          <Button label={i18n.t('common.save')} onClick={() => setSub(undefined)} status="active" />
        </ButtonContainer>
      </>
    )
  }

  return (
    <>
      <DrawerHeader>
        <ModalHeader
          left={{ name: 'close', onClick: Drawer.close }}
          title={i18n.t('filters.title')}
          right={{
            label: i18n.t('common.reset'),
            onClick: onReset,
          }}
        />
      </DrawerHeader>

      <Full>{renderList()}</Full>

      <ButtonContainer>
        <Button
          label={i18n.t('common.apply')}
          onClick={() => {
            onFiltre(local)
            Drawer.close()
          }}
          status="active"
        />
      </ButtonContainer>
    </>
  )
}

export default GroupedFilterLayout

const DrawerHeader = styled(View)`
  margin: 0 16px;
  margin-top: 30px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 8px;
`

const Full = styled(View)`
  flex: 1;
`

const ButtonContainer = styled(View)`
  padding: 16px;
  background-color: ${(props) => props.theme.colors.background};
`

const LineSection = styled(View)`
  flex-flow: row wrap;
  margin-bottom: 24px;
`

const FilterContainer = styled(View)`
  margin: 8px 0;
  margin-right: 8px;
`

const Line = styled(TouchableOpacity)`
  flex-direction: row;
  margin-bottom: 24px;
  align-items: center;
`

const DateInfo = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
`

const DateContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.backgroundTertiary};
  border-radius: 8px;
  padding: 8px 16px;
  margin: 0 8px;
`
